import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { SignOffRecord } from './signOffTypes';

/**
 * Batch-fetch all sign-offs for a module in one query.
 * Returns Map<entityId, SignOffRecord> for O(1) lookup per row.
 */
export const useSignOffSummary = (projectId: string, module: string, enabled = true) => {
  return useQuery({
    queryKey: ['sign-off-summary', projectId, module],
    queryFn: async (): Promise<Map<string, SignOffRecord>> => {
      const { data, error } = await supabase
        .from('project_sign_offs' as any)
        .select(`
          id, project_id, module, entity_type, entity_id, status,
          signed_by, signed_at, note, created_at, updated_at,
          sign_method, certificate_hash, provider, provider_ref_id, signer_device
        `)
        .eq('project_id', projectId)
        .eq('module', module)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const map = new Map<string, SignOffRecord>();
      if (data) {
        // Batch-fetch signer profiles
        const signerIds = [...new Set((data as any[]).filter(d => d.signed_by).map(d => d.signed_by))];
        let signerMap = new Map<string, { id: string; full_name: string | null; avatar_url: string | null }>();
        
        if (signerIds.length > 0) {
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, full_name, avatar_url')
            .in('id', signerIds);
          if (profiles) {
            profiles.forEach(p => signerMap.set(p.id, p));
          }
        }

        // Batch-fetch attachments
        const signOffIds = (data as any[]).map(d => d.id);
        let attachmentMap = new Map<string, import('./signOffTypes').SignOffAttachment[]>();

        if (signOffIds.length > 0) {
          const { data: attachments } = await supabase
            .from('sign_off_attachments' as any)
            .select('id, sign_off_id, file_url, file_name, file_type, uploaded_by, created_at')
            .in('sign_off_id', signOffIds)
            .order('created_at', { ascending: true });

          if (attachments) {
            (attachments as any[]).forEach(att => {
              const list = attachmentMap.get(att.sign_off_id) || [];
              list.push(att as any);
              attachmentMap.set(att.sign_off_id, list);
            });
          }
        }

        (data as any[]).forEach(row => {
          map.set(row.entity_id, {
            ...row,
            signer: row.signed_by ? signerMap.get(row.signed_by) || null : null,
            attachments: attachmentMap.get(row.id) || [],
          } as SignOffRecord);
        });
      }

      return map;
    },
    enabled: enabled && !!projectId && !!module,
    staleTime: 30_000,
  });
};
